import React, { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import classes from "./Products.module.css";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../Configuration/axiosInstance";
import SuccessDialogue from "../../Components/Shared/SuccessDialog/SuccessDialog";
import { FaTrash, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import LoadingSpinner from "../../Components/Shared/Loader/Loader";

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [changed, setDataChanged] = useState(false);
  const [count, setCount] = useState();
  const [sku, setSku] = useState(null);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productSelect, setProductSelect] = useState([]);
  const [msg, setMsg] = useState(" ");
  const [open, setOpen] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState();

  // ✅ NEW: State for inline editing
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // only runs once on mount
    axiosInstance.get(`products/allproducts`).then((data) => {
      const fetchedProducts = data.data.data;
      const uniqueCategories = [
        ...new Set(
          fetchedProducts.map((item) => item.category).filter(Boolean)
        ),
      ];
      setCategories(uniqueCategories);
    });
  }, []);

  useEffect(() => {
    setLoading(true);

    axiosInstance
      .get(
        `products?page=${page}&limit=${pageSize}&sku=${sku}&category=${category}`
      )
      .then((data) => {
        setProducts(data.data.data);
        setCount(data.data.count);
        setLoading(false);
      });
  }, [page, pageSize, changed, sku, category]);

  // ✅ NEW: Start editing a product
  const startEditing = (product) => {
    setEditingProductId(product._id);
    setEditingData({
      productName: product.productName,
      sku: product.sku,
      weight: product.weight,
      price: product.price,
      gst: product.gst,
      category: product.category,
      productType: product.productType,
      multiplier: product.multiplier || 1,
    });
  };

  // ✅ NEW: Cancel editing
  const cancelEditing = () => {
    setEditingProductId(null);
    setEditingData({});
  };

  // ✅ NEW: Handle input changes during editing
  const handleEditingChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ✅ FIXED: Save the edited product using POST method as per the route
  const saveProduct = async (productId) => {
    setUpdating(true);
    try {
      // ✅ FIXED: Use POST method instead of PATCH to match the route
      const response = await axiosInstance.post(
        `products/${productId}`,
        editingData
      );

      // Update the local state
      setProducts((prev) =>
        prev.map((product) =>
          product._id === productId ? { ...product, ...editingData } : product
        )
      );

      setMsg("Product updated successfully!");
      setOpen(true);
      setEditingProductId(null);
      setEditingData({});
    } catch (error) {
      console.error("Error updating product:", error);
      setMsg(`Failed to update product: ${error.response?.data?.message || error.message}`);
      setOpen(true);
    } finally {
      setUpdating(false);
    }
  };

  // Updated table headers to include product type
  const tableHeaders = [
    "Image",
    "Name",
    "SKU",
    "Product ID",
    "Type", // ✅ NEW
    "Weight (kg)",
    "Wholesale Price",
    "GST(% Percentage)",
    "Category",
    "Actions",
  ];

  const handleNext = () => {
    setPage((preValue) => preValue + 1);
  };
  const handlePrevious = () => {
    setPage((preValue) => preValue - 1);
  };

  const optionsHandler = (e) => {
    setCategory(e.target.value);
  };
  const handleClose = () => {
    setOpen(false);
    setDataChanged(!changed);
  };

  // Helper function to format weight display
  const formatWeight = (weight) => {
    if (!weight || weight === 0) {
      return <span style={{ color: "#999", fontStyle: "italic" }}>N/A</span>;
    }
    return (
      <span style={{ fontWeight: "600", color: "#2c3e50" }}>{weight} kg</span>
    );
  };

  // ✅ NEW: Helper function to format product type
  const formatProductType = (product) => {
    if (product.productType === "bulk") {
      return (
        <div className={classes.productTypeCell}>
          <span className={`${classes.productTypeBadge} ${classes.bulk}`}>
            📦 Bulk (x{product.multiplier})
          </span>
        </div>
      );
    }
    return (
      <div className={classes.productTypeCell}>
        <span className={`${classes.productTypeBadge} ${classes.individual}`}>
          🔵 Individual
        </span>
      </div>
    );
  };

  // Add this useEffect to handle preserved state when returning from update page
  useEffect(() => {
    // ✅ NEW: Check if we're returning with preserved state
    if (location.state?.preserveState) {
      const preservedState = location.state;

      // Restore the previous state
      setPage(preservedState.page || 1);
      setPageSize(preservedState.pageSize || 20);
      setSku(preservedState.sku || null);
      setCategory(preservedState.category || null);
      setSearchCriteria(preservedState.searchCriteria || null);
      setClearFilters(Boolean(preservedState.searchCriteria));

      // Clear the state from location to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const updateOrder = (i) => {
    // ✅ NEW: Pass current page state and filters to the update page
    const navigationState = {
      product: i,
      returnTo: {
        page: page,
        pageSize: pageSize,
        sku: sku,
        category: category,
        searchCriteria: searchCriteria,
        path: "/products",
      },
    };
    navigate("/products/updateProduct", { state: navigationState });
  };
  const inputHandler = (event) => {
    setSku(event.target.value);
  };

  const searchProduct = (i) => {
    setPage(1);
    if (sku) {
      setSearchCriteria((data) => ({ ...data, sku: sku }));
    }
    if (category) {
      setSearchCriteria((data) => ({ ...data, category: category }));
    }
    setDataChanged(!changed);
    setClearFilters(true);
  };
  const filterHandler = (btnAction) => {
    if (btnAction === "sku") {
      setSearchCriteria((preValue) => ({ ...preValue, sku: null }));
      setSku("");
    }
    if (btnAction === "category") {
      setSearchCriteria((preValue) => ({ ...preValue, category: null }));
      setCategory(null);
    }
    setDataChanged(!changed);
  };

  const deleteOrder = (id) => {
    axiosInstance.delete(`products/${id}`).then((res) => {
      setMsg(res.data.message);
      setOpen(!open);
    });
  };

  // ✅ NEW: Render editable cell
  const renderEditableCell = (product, field, type = "text") => {
    const isEditing = editingProductId === product._id;

    if (!isEditing) {
      // Display mode
      switch (field) {
        case "weight":
          return formatWeight(product[field]);
        case "productType":
          return formatProductType(product);
        default:
          return product[field] || "N/A";
      }
    }

    // Edit mode
    switch (field) {
      case "category":
        return (
          <select
            value={editingData[field] || ""}
            onChange={(e) => handleEditingChange(field, e.target.value)}
            className={classes.editInput}
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        );
      case "productType":
        return (
          <div>
            <select
              value={editingData.productType || "individual"}
              onChange={(e) => handleEditingChange("productType", e.target.value)}
              className={classes.editInput}
            >
              <option value="individual">Individual</option>
              <option value="bulk">Bulk</option>
            </select>
            {editingData.productType === "bulk" && (
              <input
                type="number"
                placeholder="Multiplier"
                value={editingData.multiplier || 1}
                onChange={(e) =>
                  handleEditingChange("multiplier", parseInt(e.target.value))
                }
                className={classes.editInput}
                style={{ marginTop: "5px", width: "80px" }}
              />
            )}
          </div>
        );
      case "weight":
      case "price":
      case "gst":
        return (
          <input
            type="number"
            step="0.01"
            value={editingData[field] || ""}
            onChange={(e) =>
              handleEditingChange(field, parseFloat(e.target.value))
            }
            className={classes.editInput}
          />
        );
      default:
        return (
          <input
            type={type}
            value={editingData[field] || ""}
            onChange={(e) => handleEditingChange(field, e.target.value)}
            className={classes.editInput}
          />
        );
    }
  };

  return (
    <div>
      <SuccessDialogue show={open} msg={msg} handleClose={handleClose} />
      <h2>All Products</h2>
      <div className={classes.searchStrip}>
        <label>SEARCH BY SKU</label>
        <input type="Text" onChange={inputHandler} value={sku} />
        <label>SEARCH BY CATEGORY</label>
        <select
          name="category"
          onChange={optionsHandler}
          value={category || ""}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <button className="searchBtn" onClick={() => searchProduct()}>
          Search
        </button>
        {clearFilters && (
          <div>
            {searchCriteria?.sku && (
              <span
                className="searchSpan"
                style={{ marginRight: "15px", marginLeft: "5px" }}
              >
                SKU: {searchCriteria.sku}
                <button
                  className="spanBtn"
                  onClick={() => filterHandler("sku")}
                >
                  {" "}
                  &times;
                </button>
              </span>
            )}
            {searchCriteria?.category && (
              <span className="searchSpan">
                {searchCriteria.category}
                <button
                  className="spanBtn"
                  onClick={() => filterHandler("category")}
                >
                  &times;
                </button>
              </span>
            )}
          </div>
        )}
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <div>
          <div className={classes.tableContainer}>
            <table className={classes.fixedTables}>
              <thead className={classes.fixedHeader}>
                <tr>
                  {tableHeaders.map((i, index) => (
                    <th key={index}>{i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products?.map((product, index) => (
                  <tr
                    key={index}
                    className={editingProductId === product._id ? classes.editingRow : ""}
                  >
                    <td>
                      <img
                        alt="product"
                        src={product.imageUrl}
                        className={classes.image}
                      />
                    </td>
                    <td>{renderEditableCell(product, "productName")}</td>
                    <td>{renderEditableCell(product, "sku")}</td>
                    <td>{product.productId}</td>
                    <td>{renderEditableCell(product, "productType")}</td>
                    <td className={classes.weightColumn}>
                      {renderEditableCell(product, "weight")}
                    </td>
                    <td>{renderEditableCell(product, "price")}</td>
                    <td>{renderEditableCell(product, "gst")}</td>
                    <td>{renderEditableCell(product, "category")}</td>
                    <td>
                      <div className="actionButtons">
                        {editingProductId === product._id ? (
                          // ✅ NEW: Editing mode buttons
                          <>
                            <button
                              className="saveBtn"
                              onClick={() => saveProduct(product._id)}
                              disabled={updating}
                              style={{
                                backgroundColor: "#28a745",
                                color: "white",
                                marginRight: "5px",
                              }}
                            >
                              <FaSave /> {updating ? "Saving..." : "Save"}
                            </button>
                            <button
                              className="cancelBtn"
                              onClick={cancelEditing}
                              style={{
                                backgroundColor: "#dc3545",
                                color: "white",
                              }}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </>
                        ) : (
                          // ✅ NEW: View mode buttons
                          <>
                            <button
                              className="editBtn"
                              onClick={() => startEditing(product)}
                              style={{ marginRight: "5px" }}
                            >
                              <FaEdit /> Quick Edit
                            </button>
                            <button
                              className="editBtn"
                              onClick={() => updateOrder(product)}
                              style={{ marginRight: "5px" }}
                            >
                              Full Edit
                            </button>
                            <button
                              className="deleteBtn"
                              onClick={() =>
                                window.confirm(
                                  "Are you sure you want to delete this Product?"
                                ) &&
                                deleteOrder(product._id)
                              }
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={"pagination"}>
            <button
              disabled={page === 1}
              onClick={handlePrevious}
              className={"pageButton"}
            >
              Previous
            </button>
            <span className={"pages"}>
              Page -<span className={"pageNumber"}>{page} </span>
            </span>
            <button
              disabled={count < pageSize}
              onClick={handleNext}
              className={"pageButton"}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Products);
